// tslint:disable: max-line-length

import {
  Component,
  Input,
  Output,
  ViewChild,
  ElementRef,
  HostListener,
  EventEmitter,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'suka-select',
  template: `
    <div class="form-item">
      <div
        [ngClass]="{
          'select--inline': display === 'inline',
          'skeleton': skeleton
        }"
        class="select"
      >
        <label *ngIf="skeleton && label" [for]="id" class="label skeleton"></label>
        <label *ngIf="!skeleton && label" [for]="id" class="label">
          <ng-container *ngIf="!isTemplate(label)">{{label}}</ng-container>
          <ng-template *ngIf="isTemplate(label)" [ngTemplateOutlet]="label"></ng-template>
        </label>
        <div *ngIf="helperText" class="form__helper-text">
          <ng-container *ngIf="!isTemplate(helperText)">{{helperText}}</ng-container>
          <ng-template *ngIf="isTemplate(helperText)" [ngTemplateOutlet]="helperText"></ng-template>
        </div>
        <div class="select-input__wrapper" [attr.data-invalid]="(invalid ? true : null)" [attr.data-valid]="(valid ? true : null)">
          <select
            #select
            [attr.id]="id"
            [disabled]="disabled"
            (change)="onChange($event)"
            [value]="value"
            (blur)="onBlur($event)"
            class="select-input">
            <ng-content></ng-content>
          </select>
          <suka-icon *ngIf="invalid" icon="alert-circle" class="select__invalid-icon"></suka-icon>
          <suka-icon *ngIf="valid" icon="check" class="select__valid-icon"></suka-icon>
          <suka-icon *ngIf="!skeleton" class="select__arrow" icon="chevron-down"></suka-icon>
        </div>
        <div *ngIf="invalid" class="form-requirement">
          <ng-container *ngIf="!isTemplate(invalidText)">{{invalidText}}</ng-container>
          <ng-template *ngIf="isTemplate(invalidText)" [ngTemplateOutlet]="invalidText"></ng-template>
        </div>
      </div>
    </div>
  `,
  styles: [`
    [data-invalid] ~ .select__arrow {
      bottom: 2.25rem;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Select,
      multi: true
    }
  ]
})
// tslint:disable-next-line: component-class-suffix
export class Select implements ControlValueAccessor {
  /**
   * Tracks the total number of selects instantiated. Used to generate unique IDs
   */
  static selectCount = 0;

  private _value = '';

  /**
   * `inline` or `default` select displays
   */
  @Input() display: 'inline' | 'default' = 'default';
  /**
   * Label for the select. Appears above the input.
   */
  @Input() label: string | TemplateRef<any>;
  /**
   * Optional helper text that appears under the label.
   */
  @Input() helperText: string | TemplateRef<any>;
  /**
   * Sets the invalid text.
   */
  @Input() invalidText: string | TemplateRef<any>;
  /**
   * Sets the unique ID. Defaults to `select-${total count of selects instantiated}`
   */
  @Input() id = `select-${Select.selectCount++}`;
  /**
   * Set to true to disable component.
   */
  @Input() disabled = false;
  /**
   * Set to true for a loading select.
   */
  @Input() skeleton = false;
  /**
   * Set to `true` for an invalid select component.
   */
  @Input() invalid = false;

  /**
   * Set to `true` for an valid select component.
   */
  @Input() valid = false;

  /**
   * emits the selected options `value`
   */
  @Output() selected = new EventEmitter();

  @ViewChild('select', {static: false}) select: ElementRef;

  get value() {
    // return this.select.nativeElement.value;
    return this._value;
  }

  set value(v) {
    this._value = v;
  }

  /**
   * Receives a value from the model.
   */
  writeValue(obj: any) {
    this.value = obj;
  }

  /**
   * Registers a listener that notifies the model when the control updates
   */
  registerOnChange(fn: any) {
    this.onChangeHandler = fn;
  }

  /**
   * Registers a listener that notifies the model when the control is blurred
   */
  registerOnTouched(fn: any) {
    this.onTouchedHandler = fn;
  }

  /**
   * Sets the disabled state through the model
   */
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  /**
   * Handles the change event from the `select`.
   * Sends events to the change handler and emits a `selected` event.
   */
  onChange(event) {
    this.onChangeHandler(event.target.value);
    this.selected.emit(event.target.value);
  }

  /**
   * Listens for the host blurring, and notifies the model
   */
  @HostListener('blur', ['$event.target'])
  onBlur(target) {
    this.onTouchedHandler();
  }

  public isTemplate(value) {
    return value instanceof TemplateRef;
  }

  /**
   * placeholder declarations. Replaced by the functions provided to `registerOnChange` and `registerOnTouched`
   */
  protected onChangeHandler = (_: any) => { };
  protected onTouchedHandler = () => { };
}
